import { RestApiClient } from 'ifield-core-components'
import log from 'loglevel'
import _ from 'lodash'
import { FUNCTION_TYPE } from '@/constants'

/**
 * サイト取得
 * @param {Number} id ID
 * @returns {Array.<Object>|Object} データ
 */
const getIndoorSites = async (id) => {
  const endPoint = id == null ? 'indoor_site' : `indoor_site/${id}`
  const url = RestApiClient.createApiUrl(endPoint)
  const json = await RestApiClient.executeGetApi(url)
  return json.data
}

/**
 * 初期スロットル間隔(ms)
 */
const INITIAL_THROTTLE_TIME = 30000

/**
 * 当モジュールで扱うデータが利用可能な権限タイプ
 */
const MY_FUNCTION_TYPE = FUNCTION_TYPE.INDOOR

export default {
  namespaced: true,
  state: {
    indoorSites: [],
    throttleTime: INITIAL_THROTTLE_TIME,
    throttler: null,
  },
  persistentStates: ['throttleTime'],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.indoorSites = []
      state.throttleTime = INITIAL_THROTTLE_TIME
      state.throttler = null
    },
    /**
     * サイトを設定する
     * @param {*} state
     * @param {Array.<Object>} indoorSites サイト
     */
    setIndoorSites(state, indoorSites) {
      state.indoorSites = indoorSites
    },
    /**
     * 特定のサイトを設定する
     * @param {*} state
     * @param {Object} indoorSite サイト
     */
    setSpecificIndoorSite(state, indoorSite) {
      const targetIndex = state.indoorSites.findIndex(
        (l) => l.id === indoorSite.id
      )
      if (targetIndex >= 0) {
        state.indoorSites[targetIndex] = indoorSite
      } else {
        state.indoorSites.push(indoorSite)
      }
    },
    /**
     * スロットル間隔を保存する
     * @param {*} state
     * @param {Number} time スロットル間隔(ms)
     */
    setThrottleTime(state, time) {
      state.throttleTime = +time
    },
    /**
     * スロットル関数を保存する
     * @param {*} state
     * @param {Function} throttler スロットル関数
     */
    setThrottler(state, throttler) {
      state.throttler = throttler
    },
  },
  getters: {
    /**
     * サイト
     * @param {*} state
     * @returns {Array.<Object>}
     */
    indoorSites: (state) => state.indoorSites,
    /**
     * サイト(Map)
     * @param {*} state
     * @returns {Map}
     */
    indoorSiteMap: (state) =>
      new Map(
        state.indoorSites.map((indoorSite) => [indoorSite.id, indoorSite])
      ),
    /**
     * 特定IDのサイトを取得
     * @param {*} state
     * @param {*} getters
     * @returns {Function} IDを引数に特定IDのサイトを返す関数
     */
    getIndoorSiteById: (state, getters) => (id) =>
      getters.indoorSiteMap.get(id),
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * IndoorSiteを更新する
     */
    async updateIndoorSites({ commit, rootGetters }) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: indoor is unavaliable. skip update')
        return
      }
      const indoorSites = await getIndoorSites()
      commit('setIndoorSites', indoorSites)
    },
    /**
     * 特定のIndoorSiteを更新する
     * @param {Number} id 対象のサイトID
     */
    async updateSpecificIndoorSite({ commit, rootGetters }, id) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: indoor is unavaliable. skip update')
        return
      }
      const indoorSite = await getIndoorSites(id)
      commit('setSpecificIndoorSite', indoorSite)
      return indoorSite
    },
    /**
     * サイトを更新する(スロットル)
     */
    async updateIndoorSitesWithThrottle({ state, commit, dispatch }) {
      if (!state.throttler) {
        const throttler = _.throttle(
          () => dispatch('updateIndoorSites'),
          state.throttleTime,
          { trailing: false }
        )
        commit('setThrottler', throttler)
      }
      return state.throttler()
    },
    /**
     * スロットル間隔更新
     * @param {Number} time スロットル間隔(ms)
     */
    updateThrottleTime({ commit }, time) {
      commit('setThrottleTime', +time)
      commit('setThrottler', null)
    },
  },
}
